import React from 'react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Dropdown } from 'react-bootstrap'
import { useSetActiveModal } from 'src/Modal/ModalStateContext'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { BasicDropdownMenuItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { GrcExportPromiseClient } from '@trustero/trustero-api-web/lib/grcexport/grcexport_grpc_web_pb'
import { LinkControlToExternalRequest } from '@trustero/trustero-api-web/lib/grcexport/grcexport_pb'
import log from 'loglevel'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { useInvalidateControlsCache } from 'src/components/async/model/control/useControls'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { getReceptorTemplate } from 'src/xgenerated/receptor'
import { Changelog, useInfoPanelContext } from '../../../../InfoPanel'
import { ModalFormId } from '../../../../components/ModalForms'
import { generatePermalinkFromId } from '../../../../components/PageLayout/Permalink'
import { useInAudit } from '../../../../context/AuditContext'
import { useCurrentControl } from '../../../../context/FormContext'
import { useRemoveControlFromRequestModal } from '../../../../Modal/useRemoveControlFromRequestModal'

export const ViewDetails = (): JSX.Element => {
  const {
    currentControl: { modelId },
  } = useCurrentControl()
  const navigate = useNavigate()
  const params = useParams()
  const onViewDetails = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      navigate(
        generatePermalinkFromId({
          pageContext: params.pageContext as string,
          modelId,
          modelType: MODEL_TYPE.CONTROL,
          isInternalLink: true,
        }),
      )
    },
    [modelId, navigate, params.pageContext],
  )

  return (
    <BasicDropdownMenuItem eventKey="1" onClick={onViewDetails}>
      View Details
    </BasicDropdownMenuItem>
  )
}

export const InScopeItems = ({
  control,
}: {
  control: Control.AsObject
}): JSX.Element => {
  const canAddEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_CONTROL_EVIDENCE,
    control,
  )
  const openAddEvidenceModal = useSetActiveModal(ModalFormId.ADD_EVIDENCE)

  if (!canAddEvidence) {
    return <></>
  }

  return (
    <>
      <BasicDropdownMenuItem
        eventKey="2"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          openAddEvidenceModal()
        }}
      >
        Add Evidence
      </BasicDropdownMenuItem>
      <Dropdown.Divider />
    </>
  )
}

export const RemoveControlFromAudit = (): JSX.Element => {
  const { removeControl } = useCurrentControl()

  return (
    <BasicDropdownMenuItem
      eventKey="4"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        removeControl()
      }}
    >
      Remove from this Audit
    </BasicDropdownMenuItem>
  )
}

export const DeleteControl = (): JSX.Element => {
  const { auditId } = useInAudit()
  const { deleteControl } = useCurrentControl()

  return (
    <>
      {auditId && <RemoveControlFromAudit />}
      <BasicDropdownMenuItem
        eventKey="5"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          deleteControl()
        }}
      >
        Delete Control
      </BasicDropdownMenuItem>
    </>
  )
}

export const CopyLink = (): JSX.Element => {
  const params = useParams()
  const {
    currentControl: { id: controlId },
  } = useCurrentControl()
  const onCopyLink = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      await navigator.clipboard.writeText(
        generatePermalinkFromId({
          pageContext: params.pageContext as string,
          modelId: controlId,
          modelType: MODEL_TYPE.CONTROL,
          isInternalLink: false,
        }),
      )
    },
    [controlId, params.pageContext],
  )
  return (
    <BasicDropdownMenuItem eventKey="6" onClick={onCopyLink}>
      Copy Permalink
    </BasicDropdownMenuItem>
  )
}

export const ViewActivities = (): JSX.Element => {
  const { setInfoPanelState } = useInfoPanelContext()
  const {
    currentControl: { id: controlId },
  } = useCurrentControl()

  const onViewActivities = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setInfoPanelState({
        isShown: true,
        render: <Changelog subjectIds={[controlId]} />,
      })
    },
    [controlId, setInfoPanelState],
  )

  return (
    <BasicDropdownMenuItem eventKey="7" onClick={onViewActivities}>
      View Activities
    </BasicDropdownMenuItem>
  )
}

export const RemoveControl = ({
  requestId,
  control,
}: {
  requestId: string
  control: Control.AsObject
}): JSX.Element => {
  const canRemoveControl = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_REQUEST_CONTROLS,
    control,
  )
  const removeControl = useRemoveControlFromRequestModal({ requestId })
  if (!canRemoveControl) {
    return <></>
  }
  return (
    <BasicDropdownMenuItem
      eventKey="8"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        removeControl()
      }}
    >
      Remove Control
    </BasicDropdownMenuItem>
  )
}

// GRC platforms whose imported controls can be linked to a Trustero Control
// Instance from here.
export const LINKABLE_RECEPTOR_MODEL_IDS = ['trr-onspring', 'trr-smartsuite']

/**
 * LinkToExternal links an imported control that has no Trustero Control
 * Instance yet (no external id) to one in the GRC platform it came from,
 * creating that record when needed. Hidden for controls that weren't imported
 * from a linkable platform or are already linked.
 */
export const LinkToExternal = ({
  control,
}: {
  control: Control.AsObject
}): JSX.Element | null => {
  const client = useAuthorizedGrpcClient(GrcExportPromiseClient)
  const invalidateControls = useInvalidateControlsCache()
  const canUpdateControls = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_UPDATE_CONTROLS,
    control,
  )
  const canExport = useCanPerformAction(CAPABILITY.CAN_EXPORT_RECEPTOR_EVIDENCE)
  const modelId = control.importReceptorModelId
  if (
    !canUpdateControls ||
    !canExport ||
    !LINKABLE_RECEPTOR_MODEL_IDS.includes(modelId) ||
    control.externalDisplayId
  ) {
    return null
  }
  const platform = getReceptorTemplate(modelId)?.name || 'GRC Platform'

  const onLink = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      const response = await client.linkControlToExternal(
        new LinkControlToExternalRequest().setControlId(control.id),
      )
      await invalidateControls()
      showInfoToast(
        `Linked ${
          control.modelId
        } to ${platform} (${response.getExternalDisplayId()})`,
      )
    } catch (error) {
      log.error('Error linking control to external record:', error)
      showInfoToast(`Couldn't link ${control.modelId} to ${platform}.`)
    }
  }

  return (
    <BasicDropdownMenuItem eventKey="9" onClick={onLink}>
      Link to {platform}
    </BasicDropdownMenuItem>
  )
}
